import React, { useState, Fragment } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Condition, ConditionGroup, Document, ConditionStatus } from '../types';
import { ConditionRow } from './ConditionRow';
import { FolderIcon } from 'lucide-react';
interface ConditionTableProps {
  conditions: Condition[];
  groups: ConditionGroup[];
  documents: Document[];
  onStatusChange: (conditionId: string, status: ConditionStatus) => void;
  onRemove: (conditionId: string) => void;
  onGroupByNote?: (conditionIds: string[], note: string) => void;
}
export function ConditionTable({
  conditions,
  groups,
  documents,
  onStatusChange,
  onRemove,
  onGroupByNote
}: ConditionTableProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [noteText, setNoteText] = useState('');
  const toggleSelect = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  };
  const handleClear = () => {
    setSelectedIds(new Set());
    setNoteText('');
  };
  const handleSubmit = () => {
    if (noteText.trim() && selectedIds.size > 0 && onGroupByNote) {
      onGroupByNote(Array.from(selectedIds), noteText.trim());
      handleClear();
    }
  };
  if (conditions.length === 0) {
    return <div className="py-8 text-center text-slate-500 text-sm bg-white border border-slate-200 rounded-b-lg" data-id="element-158">
        No conditions found.
      </div>;
  }
  // Organize conditions: ungrouped first, then by group
  const ungrouped = conditions.filter(c => !c.groupId);
  const groupedSections = groups.map(group => ({
    group,
    conditions: conditions.filter(c => c.groupId === group.id)
  })).filter(section => section.conditions.length > 0);
  const renderRow = (condition: Condition) => <ConditionRow key={condition.id} condition={condition} linkedDocument={documents.find(d => d.conditionIds.includes(condition.id))} isSelected={selectedIds.has(condition.id)} onToggleSelect={() => toggleSelect(condition.id)} onStatusChange={status => onStatusChange(condition.id, status)} onRemove={() => onRemove(condition.id)} data-id="element-159" />;
  return <>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-id="element-160">
        {/* Table Header */}
        <div className="flex items-center py-2.5 px-4 bg-slate-600 text-white text-xs font-semibold uppercase tracking-wider" data-id="element-161">
          <div className="w-8 flex-shrink-0" data-id="element-162"></div>
          <div className="flex-1 min-w-0 pr-4" data-id="element-163">Name</div>
          <div className="w-28 flex-shrink-0 text-center" data-id="element-164">Status</div>
          <div className="w-24 flex-shrink-0 text-center" data-id="element-165">Created</div>
          <div className="w-24 flex-shrink-0 text-center" data-id="element-166">Requested</div>
          <div className="w-32 flex-shrink-0 text-center" data-id="element-167">Assigned To</div>
          <div className="w-56 flex-shrink-0 text-center pr-2" data-id="element-168">Action</div>
        </div>

        <div className="divide-y divide-slate-100" data-id="element-169">
          <AnimatePresence mode="popLayout" data-id="element-170">
            {/* Ungrouped conditions */}
            {ungrouped.map(renderRow)}

            {/* Grouped sections with inline headers */}
            {groupedSections.map(({
            group,
            conditions: groupConditions
          }) => <Fragment key={group.id} data-id="element-171">
                {/* Inline group header */}
                <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} className="flex items-center gap-3 px-4 py-2 bg-slate-50 border-t border-b border-slate-200" data-id="element-172">
                  <div className="w-8 flex-shrink-0" data-id="element-173" />
                  <div className="flex items-center gap-2 flex-1" data-id="element-174">
                    <div className="h-px flex-1 bg-slate-200" data-id="element-175" />
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-white border border-slate-200 rounded-md shadow-sm" data-id="element-176">
                      <FolderIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-177" />
                      <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider" data-id="element-178">
                        {group.name}
                      </span>
                      <span className="text-[10px] text-slate-400 font-medium ml-1" data-id="element-179">
                        {groupConditions.length}
                      </span>
                    </div>
                    <div className="h-px flex-1 bg-slate-200" data-id="element-180" />
                  </div>
                  <div className="w-56 flex-shrink-0" data-id="element-181" />
                </motion.div>

                {/* Group's conditions */}
                {groupConditions.map(renderRow)}
              </Fragment>)}
          </AnimatePresence>
        </div>
      </div>

      {/* Floating Action Bar */}
      <AnimatePresence data-id="element-182">
        {selectedIds.size > 0 && <motion.div initial={{
        y: 100,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 100,
        opacity: 0
      }} className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 shadow-[0_-4px_20px_-10px_rgba(0,0,0,0.1)]" data-id="element-183">
            <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4" data-id="element-184">
              <div className="flex items-center gap-3 min-w-[200px]" data-id="element-185">
                <span className="text-sm font-medium text-slate-700" data-id="element-186">
                  {selectedIds.size} condition
                  {selectedIds.size !== 1 ? 's' : ''} selected
                </span>
                <button onClick={handleClear} className="text-sm text-slate-500 hover:text-slate-700 transition-colors" data-id="element-187">
                  Clear
                </button>
              </div>

              <div className="flex-1 max-w-2xl" data-id="element-188">
                <input type="text" value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a note to group these conditions..." className="w-full px-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" onKeyDown={e => {
              if (e.key === 'Enter') handleSubmit();
            }} data-id="element-189" />
              </div>

              <div className="min-w-[150px] flex justify-end" data-id="element-190">
                <button onClick={handleSubmit} disabled={!noteText.trim()} className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap" data-id="element-191">
                  Group by Note
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}